import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Award, Users, Globe, Target, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import { companyInfo, distributor } from '../data/mock';
import { trustSignals } from '../data/approvedContent';
import { useMotion } from '../context/MotionContext';
import { trackCTAClick } from '../utils/analytics';
import GlitchText from '../components/ui/GlitchText';

const CompanyPage = () => {
  const { prefersReducedMotion } = useMotion();

  const handleFMSClick = () => {
    trackCTAClick('fms', 'CompanyPage');
  };

  return (
    <>
      <SEOHead
        title="Company"
        description="XION Medical - German endoscopy manufacturer since 1991. International distribution network including FMSInc. in the Philippines."
        path="/company"
      />
      <Header />
      <main className="pt-32 min-h-screen bg-black grid-bg">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Page Header */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <p className="terminal-label text-[#00FFD1] mb-4">// About XION</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {prefersReducedMotion ? 'Company' : <GlitchText text="Company" />}
            </h1>
            <p className="text-white/60 text-lg max-w-2xl">
              {trustSignals.since1991}. XION has been at the forefront of endoscopy innovation,
              delivering precision-engineered medical devices from Berlin, Germany.
            </p>
          </motion.div>

          {/* Mission Statement */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#0a0a0a] border border-[#00FFD1]/30 p-8 md:p-12 mb-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <Target className="text-[#00FFD1]" size={28} />
              <h2 className="text-2xl font-bold text-white">Our Mission</h2>
            </div>
            <p className="text-3xl md:text-4xl font-light text-white leading-relaxed mb-4">
              &quot;{companyInfo.tagline}&quot;
            </p>
            <p className="text-white/60">
              {companyInfo.subtitle}
            </p>
          </motion.div>

          {/* XION System Solutions for Endoscopy — canonical company content */}
          <motion.section
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
            data-testid="company-system-solutions"
          >
            <p className="terminal-label text-[#00FFD1] mb-4">// XION System Solutions for Endoscopy</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">System Solutions for Endoscopy</h2>
            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>For 30 years now, XION has been developing, manufacturing and distributing innovative instruments, devices and systems for applications in endoscopy / minimally invasive surgery.</p>
                <p>The product range includes rigid and flexible endoscopes, light sources, camera systems, equipment trolleys and software for patient management, documentation of findings, video processing and voice analysis.</p>
                <p>Our expertise in precision mechanics, optics, electronics and software development and close cooperation with leading users enable us to perfectly match technologies. Integrated system solutions allow the physician to work more efficiently, increase patient safety and set new standards in endoscopy.</p>
                <p>Modern production technologies, a consistently applied quality management system, and the commitment and many years of experience of our more than 100 employees ensure the outstanding quality of our products.</p>
                <p><span className="text-[#00FFD1]">All XION products are manufactured at our production facility in Berlin.</span> With several of our own representatives abroad and an international dealer network, XION is now present in more than 50 countries.</p>
              </div>
              <div className="hud-card corner-brackets rounded-lg p-6 flex items-center justify-center min-h-[260px] relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-30" />
                <img
                  src="https://www.xion-medical.com/fileadmin/_processed_/4/1/csm_Systemuebersicht-2024_web_6507c88811.png"
                  alt="XION system overview"
                  loading="lazy"
                  className="relative z-10 w-full h-auto object-contain drop-shadow-[0_0_25px_rgba(0,255,209,0.12)]"
                />
              </div>
            </div>

            {/* True Full 3D — unique XION capability */}
            <div className="mt-10 grid lg:grid-cols-2 gap-10 items-center bg-gradient-to-r from-[#00FFD1]/10 to-transparent border border-[#00FFD1]/30 rounded-lg p-8" data-testid="company-true-3d">
              <div>
                <p className="terminal-label text-[#00FFD1] mb-3">// Unique Capability</p>
                <h3 className="text-2xl font-bold text-white mb-3">True Full 3D Visualisation</h3>
                <p className="text-white/70 leading-relaxed">
                  All modules of a XION system are harmonised to each other — devices, optics and instruments form a unit
                  that adapts to your workflow. Beyond 4K imaging, XION uniquely delivers stereoscopic <span className="text-[#00FFD1]">True Full 3D</span> endoscopy
                  and stroboscopy for greater depth perception, surgical precision and patient safety.
                </p>
              </div>
              <div className="rounded-lg overflow-hidden border border-white/10 bg-black/40">
                <img
                  src="https://www.xion-medical.com/fileadmin/_processed_/f/a/csm_2D-3D-Vergleich_web_0ff1e9e281.jpg"
                  alt="XION 2D vs True 3D comparison"
                  loading="lazy"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </motion.section>

          {/* Company Narrative */}
          <motion.section
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-8">Our Story</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Founded in 1991 in Berlin, Germany, XION has established itself as a trusted
                  name in endoscopy equipment manufacturing. Our commitment to precision engineering
                  and quality has made us a preferred partner for healthcare institutions worldwide.
                </p>
                <p>
                  {trustSignals.clinicalCollaboration}, driving continuous innovation
                  and ensuring our products meet the evolving needs of modern medicine.
                </p>
              </div>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Today, XION maintains an {trustSignals.internationalDistribution.toLowerCase()},
                  bringing German-engineered endoscopy solutions to healthcare providers across the globe,
                  including the Philippines through our exclusive distributor, {distributor.shortName}.
                </p>
                <p>
                  Our comprehensive product range covers everything from visualization systems
                  to specialized endoscopes, all designed with the same dedication to quality
                  that has defined XION since its founding.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Values */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6 mb-16"
          >
            <div className="card-dark p-6">
              <Award className="text-[#00FFD1] mb-4" size={32} />
              <h3 className="text-xl font-semibold text-white mb-2">{trustSignals.madeInGermany}</h3>
              <p className="text-white/60 text-sm">
                {trustSignals.qualityStandards} ensure reliable,
                long-lasting medical devices.
              </p>
            </div>
            <div className="card-dark p-6">
              <Users className="text-[#00FFD1] mb-4" size={32} />
              <h3 className="text-xl font-semibold text-white mb-2">Clinical Partnership</h3>
              <p className="text-white/60 text-sm">
                {trustSignals.clinicalCollaboration} drives continuous
                innovation and improvement.
              </p>
            </div>
            <div className="card-dark p-6">
              <Globe className="text-[#00FFD1] mb-4" size={32} />
              <h3 className="text-xl font-semibold text-white mb-2">Global Reach</h3>
              <p className="text-white/60 text-sm">
                Through trusted partners like {distributor.shortName}, XION serves
                healthcare providers worldwide.
              </p>
            </div>
          </motion.div>

          {/* Philippine Distributor */}
          {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#00FFD1]/10 to-transparent border border-[#00FFD1]/30 p-8 md:p-12 mb-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="text-[#00FFD1]" size={28} />
              <div>
                <p className="terminal-label text-[#00FFD1]">Philippine Partner</p>
                <h3 className="text-2xl font-bold text-white">Exclusive Distributor</h3>
              </div>
            </div>
            <p className="text-white/70 mb-6 max-w-2xl">
              In the Philippines, XION products are exclusively distributed by {distributor.name}.
              Our partnership ensures local expertise, dedicated support, and genuine product availability
              for Philippine healthcare providers.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={distributor.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleFMSClick}
                className="btn-primary"
              >
                Visit FMSInc.
                <ExternalLink size={16} />
              </a>
              <Link to="/contact-ph" className="btn-secondary">
                Contact for Philippines
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0a0a0a] border border-white/10 p-8 text-center"
          >
            <h3 className="text-2xl font-semibold text-white mb-4">
              Learn More About XION
            </h3>
            <p className="text-white/60 mb-6">
              Visit the official XION website or contact our Philippine distributor.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://www.xion-medical.com/en/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                XION Global Website
                <ExternalLink size={16} />
              </a>
              <Link to="/contact-ph" className="btn-primary">
                Contact Philippines
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CompanyPage;
